import { useState } from "react";
import { useI18n } from "../../i18n";
import { cn } from "../cn";
import { useOnboarding } from "./useOnboarding";

/**
 * OnboardingContextGuide — Macro #27 small, non-invasive contextual guide shown
 * INSIDE a real product surface (the Canvas editor) when the guided setup is
 * active for THIS territory. It never re-implements the surface: it only nudges
 * and links back to the control center. Closable; honest (only the real
 * "Draft saved" state ticks — no fake completion).
 */
export function OnboardingContextGuide({ identityId, className }: { identityId: string; className?: string }) {
  const { t } = useI18n();
  const onb = useOnboarding();
  const [hidden, setHidden] = useState(false);

  const active =
    onb.status === "ready" &&
    onb.state?.selected_territory === identityId &&
    !onb.state?.completed;

  if (!active || hidden) return null;

  const draftSaved = (onb.canonical?.selected_territory?.draft_revision ?? 0) > 0;
  const items: Array<{ key: string; done: boolean }> = [
    { key: "identity", done: false },
    { key: "media", done: false },
    { key: "social", done: false },
    { key: "save", done: draftSaved },
  ];

  return (
    <div
      data-testid="onboarding-context-guide"
      className={cn("rounded-md border border-border-accent bg-panel/95 neon-glass p-3.5 shadow-glow-teal-sm", className)}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-neon-teal">{t("onboarding.guide.title")}</span>
        <button
          data-testid="onboarding-guide-hide"
          onClick={() => setHidden(true)}
          className="font-mono text-[9px] tracking-[0.14em] uppercase text-text-muted hover:text-text-primary"
        >
          {t("onboarding.guide.hide")}
        </button>
      </div>
      <div className="mt-1 font-display text-[13px] font-semibold text-text-primary">{t("onboarding.guide.heading")}</div>
      <p className="mt-0.5 font-display text-[11.5px] leading-[1.5] text-text-secondary">{t("onboarding.guide.intro")}</p>
      <ul className="mt-2.5 space-y-1.5">
        {items.map((it) => (
          <li key={it.key} data-testid={`onboarding-guide-item-${it.key}`} data-done={it.done} className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border text-[9px]",
                it.done ? "border-border-accent bg-neon-teal-soft text-neon-teal" : "border-border-soft text-text-muted",
              )}
            >
              {it.done ? "✓" : ""}
            </span>
            <span className={cn("font-display text-[11.5px]", it.done ? "text-text-primary" : "text-text-secondary")}>
              {t(`onboarding.guide.items.${it.key}`)}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-2">
        <a
          data-testid="onboarding-guide-back"
          href="#onboarding"
          className="inline-flex items-center h-7 px-3 no-underline rounded-md border border-neon-teal text-neon-teal font-mono text-[10px] uppercase tracking-[0.14em] hover:bg-neon-teal-soft"
        >
          {t("onboarding.guide.back")}
        </a>
      </div>
    </div>
  );
}
